import React, { useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, Archive, CalendarClock, PencilIcon, RefreshCw } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import { skipToken } from '@reduxjs/toolkit/query';
import { engineErrorMessage, type EngineError } from 'src/shared/state/api-slices/engineErrorMessage';
import InstantChips, { type Instant } from 'src/shared/components/instant-chips/InstantChips';
import RangePicker from 'src/shared/components/range-picker/RangePicker';
import { CUSTOM_RANGE, DEFAULT_RANGE, rangeForPreset } from 'src/shared/components/range-picker/timeRangePresets';
import {
    useGetStacCollectionQuery,
    useGetStacItemTileLayersQuery,
    useGetStacItemValidAtQuery,
    useListStacItemsQuery,
    type StacItem,
    type StacProvider,
} from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { hasTileArchive } from 'src/shared/catalogue/tileSource';
import AssetTable from './components/asset-list/AssetTable';
import VersionTimeline from './components/version-timeline/VersionTimeline';
import CatalogueMap from './components/catalogue-map/CatalogueMap';
import {
    instantThatSelects,
    segmentInForceAt,
    validitySegmentsOf,
    versionInForceAt,
    type TimeRange,
} from './domain/versions/versionTimeline';
import { versionName } from './domain/versions/versionName';

const card = 'rounded-xl border border-border bg-white p-6 shadow-portfolio-summary-card';

const messagePanel = (testId: string, heading: string, detail: string) => (
    <div className='flex flex-col items-center justify-center gap-4 py-24 text-center' data-testid={testId}>
        <div className='flex h-16 w-16 items-center justify-center rounded-full bg-red-0'>
            <AlertCircle className='h-8 w-8 text-red-100' />
        </div>
        <div className='flex max-w-md flex-col items-center gap-1'>
            <h3 className='text-lg font-semibold text-primary'>{heading}</h3>
            <p className='text-sm text-muted-foreground'>{detail}</p>
        </div>
    </div>
);

/**
 * Who had a hand in the data, and what they did.
 *
 * Beside the licence rather than folded into it: `license` has a closed vocabulary and says
 * only how the data may be used, while `providers` is STAC's field for who made it. The roles
 * are shown because a licensor is not a producer, and the difference matters. A `description`
 * is where STAC keeps the wording a licensor asks to be credited with, so it is shown too.
 */
const providerList = (providers: StacProvider[]) => (
    <ul className='flex flex-col gap-1' data-testid='collection-providers'>
        {providers.map(provider => (
            <li key={provider.name} className='flex flex-col gap-0.5'>
                <span className='flex flex-wrap items-baseline gap-x-2'>
                    {provider.url ? (
                        <a href={provider.url} target='_blank' rel='noreferrer' className='font-medium text-primary hover:underline'>
                            {provider.name}
                        </a>
                    ) : (
                        <span className='font-medium'>{provider.name}</span>
                    )}
                    {(provider.roles ?? []).length > 0 && (
                        <span className='text-xs text-muted-foreground'>{(provider.roles ?? []).join(', ')}</span>
                    )}
                </span>
                {/* The wording a licensor asks to be credited with. Stored but never shown, it
                    would be a promise to a data owner that nobody keeps. */}
                {provider.description && <span className='text-xs text-muted-foreground'>{provider.description}</span>}
            </li>
        ))}
    </ul>
);

/**
 * The three instants a version carries, as chips.
 *
 * An instant the version does not state is left out rather than shown empty: `created` and
 * `updated` are the engine's, and a document stored before it wrote them has neither.
 */
const instantsOf = (version: StacItem): Instant[] =>
    [
        { key: 'computed', label: 'Computed', icon: CalendarClock, at: version.properties.datetime },
        { key: 'catalogued', label: 'Catalogued', icon: Archive, at: version.properties.created },
        { key: 'revised', label: 'Revised', icon: RefreshCw, at: version.properties.updated },
    ].filter((instant): instant is Instant => typeof instant.at === 'string');

const definition = (term: string, value: React.ReactNode) => (
    <div className='flex flex-col gap-0.5'>
        <dt className='text-xs uppercase tracking-wide text-muted-foreground'>{term}</dt>
        <dd className='text-sm text-foreground'>{value}</dd>
    </div>
);

/**
 * One dataset: what it declares, when its versions arrived, and what one of them holds.
 *
 * The timeline is the way in. The engine has no "list every version" route — it answers which
 * versions were *computed* in a window (ADR-016 decision 2, as amended) — so the window is the
 * query, and it opens on the dataset's own declared coverage rather than on today. A
 * today-sized window would be empty for every monthly dataset we hold.
 */
const StacCatalogueDetail = () => {
    const { collectionId = '' } = useParams<{ collectionId: string }>();
    const navigate = useNavigate();
    const { data: collection, isLoading, error } = useGetStacCollectionQuery(collectionId);

    const [preset, setPreset] = useState<string>(DEFAULT_RANGE);
    const [custom, setCustom] = useState<TimeRange | null>(null);
    /** When this page was opened. Fixed, so the instant it defaults to does not creep. */
    const [openedAt] = useState(() => new Date());
    /** Where the marker has been put, if it has. `null` until then — see `atInstant`. */
    const [chosenInstant, setChosenInstant] = useState<Date | null>(null);

    // The span on screen: a preset counted back from now, or the two dates a custom span names.
    const presetRange = useMemo(() => rangeForPreset(preset, new Date()), [preset]);
    const period = presetRange ?? custom ?? { from: new Date(), to: new Date() };

    const windowFrom = moment.utc(period.from).startOf('day');
    const windowTo = moment.utc(period.to).endOf('day');

    const { data: listed, isLoading: isLoadingVersions } = useListStacItemsQuery({
        collectionId,
        from: windowFrom.toISOString(),
        to: windowTo.toISOString(),
    });

    // The other axis: which version the engine serves (ordering, not containment). Asked at the
    // window's start, because a version computed before it can still hold throughout it and
    // would otherwise leave the track blank.
    const { data: inForceAtStart } = useGetStacItemValidAtQuery({ collectionId, validAt: windowFrom.toISOString() });

    const versions = listed?.features ?? [];
    const timelineRange = { from: windowFrom.toDate(), to: windowTo.toDate() };

    /**
     * Where the marker sits: where it was put, else where the version in force began.
     *
     * Opening at today would show the current version at its last moment, with its whole span
     * behind the marker. Opening where it came into force shows the version, and the bar under
     * the marker reads as the whole of it.
     *
     * The spans are built here without knowing which version is in force, because that answer
     * comes from a query keyed on the very instant being derived. It makes no difference to this
     * question: whether the last bar ends at the window's edge or runs past it does not change
     * which bar contains `openedAt`.
     */
    const spans = validitySegmentsOf({ versions, carriedIn: inForceAtStart?.features?.[0], range: timelineRange });
    const atInstant = chosenInstant ?? segmentInForceAt(spans, openedAt)?.from ?? openedAt;

    // What the map draws: whatever the engine serves at the marker's instant.
    const { data: inForceAtInstant } = useGetStacItemValidAtQuery({ collectionId, validAt: atInstant.toISOString() });
    const drawn = inForceAtInstant?.features ?? [];

    /**
     * The layers inside the archive of whatever is on the map.
     *
     * Asked of the first version carrying one: a snapshot dataset resolves to exactly one at
     * any instant, which is every dataset we hold. A mosaic resolves to many and would need one
     * request each — a loop of hooks this cannot do, and a batched route it does not have yet.
     */
    const firstArchive = drawn.find(hasTileArchive);
    const { data: archiveLayers, error: layersError } = useGetStacItemTileLayersQuery(
        firstArchive ? { collectionId, itemId: firstArchive.id } : skipToken
    );
    // An empty record until the engine answers, so "not asked yet" and "answered none" stay
    // distinguishable — the map warns only about the second.
    const servedLayers = firstArchive && archiveLayers ? { [firstArchive.id]: archiveLayers.layers } : {};
    const inForce = inForceAtInstant?.features?.[0];
    const matched = listed?.numberMatched ?? versions.length;
    const isTruncated = matched > versions.length;

    // The axis *is* the span that was chosen: there is nothing to drag on it any more, so it
    // has no reason to be wider than what was asked for.
    const bounds = period;

    const segments = validitySegmentsOf({
        versions,
        carriedIn: inForceAtStart?.features?.[0],
        range: timelineRange,
        inForceId: inForce?.id,
    });

    /**
     * What is on show: the version the marker is standing on.
     *
     * Derived rather than picked. The map draws the marker's instant, so anything the panel
     * could be pointed at independently would be a second answer to the same question — and the
     * two could disagree. Read off the timeline rather than from the engine's reply so it is
     * immediate: a marker dragged across a boundary renames the panel without a round trip.
     */
    const selected = versionInForceAt(segments, atInstant);

    /** Picking a version is a statement about *when*: it moves the marker, and selection follows. */
    const pick = (version: StacItem) => {
        const segment = segments.find(candidate => candidate.version.id === version.id);
        if (segment) {
            setChosenInstant(instantThatSelects(segment, atInstant));
        }
    };

    const changePreset = (next: string) => {
        // A custom span opens on whatever was already on screen, so it is a starting point
        // rather than a blank pair of fields.
        if (next === CUSTOM_RANGE) {
            setCustom(period);
        }
        setPreset(next);
    };

    const changeCustom = (next: TimeRange) => {
        setCustom(next);
    };

    /**
     * What the version on the map holds, floated over it by `CatalogueMap`.
     *
     * The list above browses the period; this is the one version picked out of it. It carries
     * no map of its own: what is drawn belongs to the instant the marker points at, which may
     * hold several versions at once.
     */
    const selectedPanel = selected && (
        <section className='rounded-xl border border-border bg-white p-4' data-testid='selected-version'>
            <div className='mb-3 flex items-start justify-between gap-3'>
                <div className='flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1'>
                    <h3 className='break-words text-base font-semibold text-foreground' data-testid='selected-version-id'>
                        {versionName(selected)}
                    </h3>
                    {versionName(selected) !== selected.id && (
                        <code
                            className='rounded bg-grey-10 px-1.5 py-0.5 text-[11px] text-muted-foreground'
                            data-testid='selected-version-identifier'
                        >
                            {selected.id}
                        </code>
                    )}
                </div>

                <div className='flex shrink-0 items-center gap-2' data-testid='selected-version-controls'>
                    <InstantChips instants={instantsOf(selected)} testId='selected-version-instants' />
                    <Link
                        to={`/admin/catalogue/${collectionId}/versions/${selected.id}/edit`}
                        className='text-sm font-medium text-primary hover:underline'
                        data-testid='stac-item-edit'
                        title='Edit version'
                    >
                        <PencilIcon className='h-3.5 w-3.5' aria-hidden='true' />
                    </Link>
                </div>
            </div>

            <h4 className='mb-2 text-sm font-semibold text-foreground'>Assets</h4>
            <AssetTable assets={selected.assets ?? {}} />
        </section>
    );

    const mainContent = (() => {
        if (isLoading) {
            return (
                <div className='flex justify-center py-24' data-testid='stac-collection-loading'>
                    <Loader />
                </div>
            );
        }

        // A dataset that was never established reads differently from one the engine could not
        // fetch: only the first is the super user's to fix.
        if (error && 'status' in error && error.status === 404) {
            return messagePanel('stac-collection-not-found', 'No such dataset', `The catalogue holds no dataset called "${collectionId}".`);
        }

        if (error) {
            return messagePanel('stac-collection-error', 'Unable to load this dataset', 'The engine returned an error.');
        }

        return (
            <div className='flex flex-col gap-6'>
                <section className={card} data-testid='collection-summary'>
                    <p className='mb-4 text-sm text-muted-foreground'>{collection?.description}</p>
                    <dl className='grid grid-cols-2 gap-6 md:grid-cols-4'>
                        {definition('Identifier', <code className='rounded bg-secondary px-2 py-1 text-xs'>{collection?.id}</code>)}
                        {definition('Licence', collection?.license)}
                        {(collection?.providers ?? []).length > 0 &&
                            definition(
                                (collection?.providers ?? []).length > 1 ? 'Providers' : 'Provider',
                                providerList(collection?.providers ?? [])
                            )}
                        {definition(
                            'Declared coverage',
                            `${
                                collection?.extent?.temporal?.interval?.[0]?.[0]
                                    ? moment.utc(collection.extent.temporal.interval[0][0] as string).format('D MMM YYYY')
                                    : 'Not stated'
                            } → ${
                                collection?.extent?.temporal?.interval?.[0]?.[1]
                                    ? moment.utc(collection.extent.temporal.interval[0][1] as string).format('D MMM YYYY')
                                    : 'ongoing'
                            }`
                        )}
                    </dl>
                    {collection?.keywords && collection.keywords.length > 0 && (
                        <div className='mt-4 flex flex-wrap gap-1'>
                            {collection.keywords.map(keyword => (
                                <span key={keyword} className='rounded bg-secondary px-2 py-0.5 text-xs text-muted-foreground'>
                                    {keyword}
                                </span>
                            ))}
                        </div>
                    )}
                </section>

                <section className='flex flex-col gap-4'>
                    <div className='flex flex-wrap items-end justify-between gap-4'>
                        <div>
                            <div className='flex items-center gap-2'>
                                <h3 className='text-lg font-semibold text-foreground'>Versions</h3>
                                <span
                                    className='rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground'
                                    data-testid='timeline-version-count'
                                >
                                    {matched}
                                </span>
                            </div>
                            <p className='text-sm text-muted-foreground' data-testid='versions-subtitle'>
                                Every catalogued dataset can hold multiple versions over time. The timeline shows how long each was the one
                                in force — choose a span to change which are listed, and move the marker to change the instant the map
                                displays.
                            </p>
                        </div>
                    </div>

                    {isTruncated && (
                        <div
                            className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2'
                            data-testid='timeline-truncated'
                            role='status'
                        >
                            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
                            <p className='text-xs text-foreground'>
                                Showing {versions.length} of <span className='font-semibold'>{matched}</span> versions — the engine caps a
                                listing. Narrow the window to see the rest.
                            </p>
                        </div>
                    )}

                    {isLoadingVersions ? (
                        <div className='flex justify-center py-12'>
                            <Loader />
                        </div>
                    ) : (
                        <VersionTimeline
                            segments={segments}
                            bounds={bounds}
                            controls={
                                <RangePicker preset={preset} period={period} onPresetChange={changePreset} onCustomChange={changeCustom} />
                            }
                            selectedId={selected?.id}
                            atInstant={atInstant}
                            onAtChange={setChosenInstant}
                            onSelect={pick}
                        />
                    )}

                    <CatalogueMap
                        items={drawn}
                        collectionId={collectionId}
                        atInstant={atInstant}
                        detail={selectedPanel}
                        servedLayers={servedLayers}
                        layersError={engineErrorMessage(layersError as EngineError | undefined)}
                    />
                </section>
            </div>
        );
    })();

    return (
        <StandardAppLayout
            title={collection?.title || collectionId}
            // The way back is a breadcrumb, which this layout puts on the left ahead of the
            // title — where a reader looks for it — rather than among the actions on the right.
            breadcrumbs={[{ label: 'Geospatial Catalogue', path: '/admin/catalogue' }, { label: collection?.title || collectionId }]}
            otherActions={
                collection && (
                    <div className='flex items-center gap-3'>
                        <SecondaryButton
                            onClick={() => navigate(`/admin/catalogue/${collectionId}/edit`)}
                            data-testid='stac-collection-edit'
                        >
                            Edit dataset
                        </SecondaryButton>
                        <PrimaryButton
                            onClick={() => navigate(`/admin/catalogue/${collectionId}/versions/new`)}
                            data-testid='stac-item-new'
                        >
                            Publish a version
                        </PrimaryButton>
                    </div>
                )
            }
            mainContent={mainContent}
        />
    );
};

export default StacCatalogueDetail;
